import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { MAX_WITHDRAWAL_AMOUNT } from '~/entities/Balance/constants'
import { rubleMaskOptions } from '~/shared/lib/maskito'
import { priceFormat, sanitizePriceNumber } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import { FormProvider, NumericInput } from '~/shared/ui/form'
import { FormValues, withdrawalSchema } from './model'
import { FieldWrapper, RemoveAllButton } from './styled'

type FormProps = {
  isSubmitting: boolean
  onSuccess: () => void
  amountAll: number
  isAmountAllLoading: boolean
  onSumChange: (sum?: number) => void
  disable: boolean
  additionalContent?: ReactNode
}

export function WithdrawalForm({
  isSubmitting,
  onSuccess,
  amountAll,
  isAmountAllLoading,
  onSumChange,
  disable,
  additionalContent,
}: FormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(withdrawalSchema),
  })

  const { watch, reset } = form

  const amountForm = watch('amount')

  const amountError = useMemo(() => {
    const amount = sanitizePriceNumber(amountForm || 0)
    let message = ''

    if (amount > MAX_WITHDRAWAL_AMOUNT) {
      message = `Максимальная сумма за разовый вывод средств ${priceFormat(
        MAX_WITHDRAWAL_AMOUNT,
      )} руб.`
    } else if (amount > amountAll) {
      message = `Допустимый остаток списания - ${amountAll.toLocaleString()} руб.`
    }

    return message || undefined
  }, [amountAll, amountForm])

  watch((data, { name, type }) => {
    if (type !== 'change') return
    if (name === 'amount') {
      onSumChange(sanitizePriceNumber(data.amount || 0))
    }
  })

  const handleWithdrawAll = () => {
    reset({ amount: amountAll })
    onSumChange(amountAll)
  }

  const handleOnSuccess = () => {
    if (amountError) return
    onSuccess()
  }

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <FieldWrapper>
        <NumericInput
          name='amount'
          label='Сумма для снятия'
          errorText={amountError}
          maskitoOptions={rubleMaskOptions}
        >
          <RemoveAllButton
            loading={isAmountAllLoading}
            onClick={handleWithdrawAll}
            variant='textLink'
          >
            Снять всё
          </RemoveAllButton>
        </NumericInput>
      </FieldWrapper>

      {additionalContent}

      <Button
        fullWidth
        type='submit'
        variant='limeFlooded'
        size='large'
        loading={isSubmitting}
        disabled={disable}
        data-testid='withdrawal-drawer-submit'
      >
        Вывести
      </Button>
    </FormProvider>
  )
}
